import { useCallback, useMemo, useRef } from 'react';
import '../Analitos.scss';
import DataGrid, { Column, Button as Butt, Editing, RequiredRule, Form, Toolbar, Item as ItemT, SearchPanel, Scrolling, Paging, Pager, Popup as Pop } from 'devextreme-react/data-grid';
import { useApiConfiguration } from '../../../api/api.configuration';
import { Item } from 'devextreme-react/form';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { Button, Tooltip } from 'devextreme-react';
import notify from 'devextreme/ui/notify';
import { useScreenSize } from '../../../utils/media-query';

export const AlternateRanks = ({ dataRanks, update }) => {
  const dataRef = useRef<DataGrid>(null);
  const { isMedium, isSmall, isXSmall } = useScreenSize();
  const { newAlternateRanks, setAlternateRanks, deleteAlternateRanks } = useApiConfiguration();

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Ra_Id',
      load() {
        return Promise.resolve({
          data: dataRanks,
        }).catch(() => {
          throw new Error('Error de carga de datos');
        });
      },
      insert: async (values) => {
        const res = await newAlternateRanks({ Rango: values });
        if (res !== 105) throw new Error('Error insertando el rango alterno');
        update(); // refetch: la fila nueva necesita el Id generado por el back
      },
      update: async (key, values) => {
        const res = await setAlternateRanks({ RangoAlterno: values, IdRangoAlterno: key });
        if (res !== 107) throw new Error('Error actualizando el rango alterno');
        update(); // refetch (igual que el botón Refrescar) → se ve el cambio sin clic
      },
      remove: async (key) => {
        const res = await deleteAlternateRanks({ IdRangoAlterno: key });
        if (res !== 107) throw new Error('Error eliminando el rango alterno');
        // el grid quita la fila localmente, sin recargar
      },
    });
  }, [dataRanks]);

  const customDataSrc = useMemo(
    () =>
      new DataSource({
        store: customData,
        reshapeOnPush: true,
      }),
    [customData]
  );

  const handleRefresh = useCallback(() => {
    update();
  }, [update]);

  const handleDuplicate = useCallback((e: any) => {
    const rowData = e.row.data;
    if (!rowData) return;

    // Agregar nueva fila al grid
    dataRef.current?.instance.addRow();

    // Después de un pequeño delay, establecer los valores duplicados
    setTimeout(() => {
      const visibleRows = dataRef.current?.instance.getVisibleRows();
      const newRowIndex = visibleRows?.findIndex((row: any) => row.isNewRow);

      if (newRowIndex !== -1 && newRowIndex !== undefined) {
        // Establecer valores duplicados (excepto el ID)
        dataRef.current?.instance.cellValue(newRowIndex, 'Ra_Nombre', `${rowData.Ra_Nombre} - Copia`);
        dataRef.current?.instance.cellValue(newRowIndex, 'Ra_Detalle', rowData.Ra_Detalle);
        dataRef.current?.instance.cellValue(newRowIndex, 'Ra_Estado', true);
      }
    }, 100);

    notify('📋 Rango extendido duplicado. Complete la edición para guardar.', 'info', 3000);
  }, []);

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <DataGrid
        dataSource={customDataSrc}
        ref={dataRef}
        width='100%'
        height='100%'
        columnAutoWidth={true}
        columnHidingEnabled={true}
        allowColumnResizing={true}
        showBorders={true}
        rowAlternationEnabled={true}
        hoverStateEnabled={true}
        wordWrapEnabled={true}>
        <SearchPanel visible={true} width={300} placeholder='Buscar rangos...' />
        <Scrolling columnRenderingMode='standard' mode='standard' useNative={true} />
        <Paging enabled={true} defaultPageSize={25} />
        <Pager visible={true} allowedPageSizes={[25, 50, 100, 200, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />
        <Editing mode={isSmall || isXSmall || isMedium ? 'popup' : 'form'} allowUpdating={true} allowAdding={true} allowDeleting={true} selectTextOnEditStart={true} useIcons={true} texts={{ saveRowChanges: 'Guardar', cancelRowChanges: 'Cancelar', deleteRow: 'Eliminar', addRow: 'Agregar' }}>
          <Pop title='Rango extendido' showTitle={true} fullScreen={isXSmall || isSmall} width={isMedium ? '70%' : 600} height='auto' />
          <Form labelLocation='top' colCount={2} colCountByScreen={{ xs: 1, sm: 1, md: 2, lg: 2 }}>
            <Item dataField='Ra_Nombre' colSpan={1} />
            <Item dataField='Ra_Estado' editorType='dxCheckBox' colSpan={1} />
            <Item dataField='Ra_Detalle' editorType='dxTextArea' colSpan={2} editorOptions={{ height: 200 }} />
          </Form>
        </Editing>
        <Toolbar>
          <ItemT name='searchPanel' />
          <ItemT name='addRowButton' />
          <ItemT location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button id='RefrecarResult' icon='refresh' text='Refresh' stylingMode='text' onClick={handleRefresh} />
            <Tooltip target={'#RefrecarResult'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Refrescar datos
            </Tooltip>
          </ItemT>
        </Toolbar>
        <Column allowGrouping={false} caption='ID' width={80} visible={true} allowEditing={false} dataField='Ra_Id' alignment='center' hidingPriority={0} />
        <Column allowGrouping={false} caption='RANGO' minWidth={120} visible={true} allowEditing={true} dataField='Ra_Nombre' alignment='center' hidingPriority={3}>
          <RequiredRule />
        </Column>
        <Column allowGrouping={false} caption='DETALLE' minWidth={150} visible={true} allowEditing={true} dataField='Ra_Detalle' alignment='left' hidingPriority={1}>
          <RequiredRule />
        </Column>
        <Column dataType='boolean' allowGrouping={false} width={100} caption='ESTADO' visible={true} allowEditing={true} dataField='Ra_Estado' alignment='center' hidingPriority={2} />
        <Column type='buttons' caption={'ACCIÓN'} width={120}>
          <Butt name='edit' />
          <Butt name='delete' />
          <Butt icon='copy' hint='Duplicar rango extendido' onClick={handleDuplicate} />
        </Column>
      </DataGrid>
    </div>
  );
};
